/**
 * Funções puras sobre a rota que o roteador, o mapa e a UI compartilham (DE-B02, DE-11).
 * - `effectiveScene` (§9.4.1, código inteiro): nível e modo que o mapa desenha. `effectiveMapMode` (§5.1) é o `.mode`.
 * - `viewKey`, `focusUf`, `isAbroad` (§5.1) e `availableOffices` (§5.4.1): a regra mora aqui, uma vez só;
 *   `router/selectors.ts` e `router/offices.ts` reexportam, e a UI as lê pelos computeds de `state/derived.ts`.
 * A rota e as flags entram por tipos estruturais locais (`model/` não importa `config`, §2.2.2).
 */
import type { LayoutMode, MapLevel, MapMode, PlaceRoute, RouteOffice, RouteState, UfCode } from './types';

/** O que effectiveScene lê da rota. */
export interface SceneRoute {
  office: RouteOffice;
  place: { kind: PlaceRoute['kind'] };
  mapMode: MapMode;
}
/** O que effectiveScene lê das flags (`AppConfig['features']` é atribuível). */
export interface SceneFlags {
  advantage_mode: boolean;
  municipal_map: boolean;
}
export interface Scene {
  level: MapLevel;
  mode: MapMode;
}

const ABROAD_KINDS: ReadonlySet<PlaceRoute['kind']> = new Set(['abroad', 'country', 'abroad_city']);

/** §9.4.1: nível e modo efetivos. A URL guarda o modo pedido, não o efetivo. */
export function effectiveScene(
  r: SceneRoute,
  live: boolean,
  layout: LayoutMode,
  f: SceneFlags,
  geo: { mun: boolean; world: boolean },
  municipalData: boolean,
): Scene {
  const abroad = ABROAD_KINDS.has(r.place.kind);
  const focus = r.place.kind === 'uf' || r.place.kind === 'municipality';
  let mode = r.mapMode;
  const phone = layout === 'flow' || layout === 'land'; // celular: sem MapModeBar
  if (abroad) {
    return {
      level: 'world',
      mode: !phone && (mode === 'counted' || (mode === 'candidate' && live)) ? mode : 'municipalities',
    };
  }
  if (r.office !== 'president') return { level: 'uf', mode: 'states' }; // §9.4.9 e §9.4.10
  // celular (§9.14; regra de 31/10, "Sim, também no celular"): com UF em foco, Municípios; no Brasil, Municípios ou
  // Estados, escolhidos no próprio mapa (os outros modos não têm botão no celular e caem em Municípios)
  if (phone) mode = focus ? 'municipalities' : mode === 'states' ? 'states' : 'municipalities';
  if (mode === 'candidate' && !live) mode = 'municipalities'; // F91
  if (mode === 'advantage' && !f.advantage_mode) mode = 'municipalities';
  if (!f.municipal_map && mode === 'municipalities') mode = 'states';
  const canMun = f.municipal_map && geo.mun && municipalData;
  const wantsMun = focus || mode !== 'states';
  return { level: wantsMun && canMun ? 'municipal' : 'uf', mode };
}

/** Modos que, no Brasil, pintam cada município pela própria cor (§9.4.2; Estados e Vantagem não). */
export const NATIONAL_MUNICIPAL_MODES: ReadonlySet<MapMode> = new Set<MapMode>([
  'municipalities',
  'counted',
  'candidate',
]);

/**
 * O Brasil inteiro por município (regra do responsável de 31/10, §8: ao escolher "Municípios", cada município tem a
 * própria cor, nunca a do estado): aba Presidente, sem lugar em foco, num modo por município. `mode` é o modo EFETIVO
 * (`effectiveMapMode`): no celular, sem barra de modos, o Brasil é sempre Estados e nenhuma malha é pedida sem UF em foco
 * (§15.7, regra 6 do CLAUDE.md).
 */
export function municipalNational(r: Pick<RouteState, 'office' | 'place'>, mode: MapMode): boolean {
  return r.office === 'president' && r.place.kind === 'br' && NATIONAL_MUNICIPAL_MODES.has(mode);
}

/** §5.1: modo que o mapa desenha, sem depender da malha (o nível é que depende). */
export function effectiveMapMode(r: SceneRoute, live: boolean, layout: LayoutMode, f: SceneFlags): MapMode {
  return effectiveScene(r, live, layout, f, { mun: true, world: true }, true).mode;
}

/** "{office}:{placeKey}" (ex.: `president:uf:SP`). A zona não entra (§5.1). */
export function viewKey(r: Pick<RouteState, 'office' | 'place'>): string {
  const p = r.place;
  let key: string;
  switch (p.kind) {
    case 'br':
      key = 'br';
      break;
    case 'uf':
      key = `uf:${p.uf}`;
      break;
    case 'municipality':
      key = `mun:${p.ibge}`;
      break;
    case 'abroad':
      key = 'abroad';
      break;
    case 'country':
      key = `country:${p.iso2}`;
      break;
    case 'abroad_city':
      key = `city:${p.tse}`;
      break;
  }
  return `${r.office}:${key}`;
}

/** UF de `uf` ou `municipality`; senão null (§5.1). */
export function focusUf(r: Pick<RouteState, 'place'>): UfCode | null {
  const p = r.place;
  return p.kind === 'uf' || p.kind === 'municipality' ? p.uf : null;
}

/** `place.kind ∈ {abroad, country, abroad_city}` (§5.1). */
export function isAbroad(r: Pick<RouteState, 'place'>): boolean {
  return ABROAD_KINDS.has(r.place.kind);
}

/** O que availableOffices lê do contexto da rota (`RouteContext` é atribuível). */
export interface OfficesContext {
  flags: { governors: boolean; archive_1t: boolean };
  candidates: unknown;
  governorUfs: ReadonlySet<unknown>;
}

/** §5.4.1: ordem fixa Presidente, Governadores, Senado, Deputados. */
export function availableOffices(ctx: OfficesContext): RouteOffice[] {
  const list: RouteOffice[] = ['president'];
  if (ctx.flags.governors && (ctx.candidates == null || ctx.governorUfs.size > 0)) list.push('governor');
  if (ctx.flags.archive_1t) list.push('senate', 'chamber');
  return list;
}
